/**
 * A fila do que foi escrito sem rede — só o rascunho da ficha.
 *
 * `sessionStorage`, e não `localStorage`: o rascunho é dado de saúde, e num
 * tablet dividido ele não pode sobreviver a fechar a aba. A fila só é usada
 * quando `navigator.onLine` diz que não há rede; com rede, erro é erro e aparece
 * para quem digitou.
 */

import type { RespostaDaFicha } from '@/data/tipos';

const CHAVE = 'vec.fila-offline.rascunhos';

export interface RascunhoNaFila {
    atendimentoId: string;
    modeloId: string;
    respostas: Record<string, RespostaDaFicha>;
    guardadoEm: string;
}

export function semRede(): boolean {
    return typeof navigator !== 'undefined' && navigator.onLine === false;
}

function ler(): RascunhoNaFila[] {
    try {
        const bruto = sessionStorage.getItem(CHAVE);
        return bruto ? (JSON.parse(bruto) as RascunhoNaFila[]) : [];
    } catch {
        /* Armazenamento bloqueado ou conteúdo corrompido: a fila está vazia —
           quem guarda avisa quando não consegue (ver `enfileirarRascunho`). */
        return [];
    }
}

function gravar(itens: RascunhoNaFila[]): boolean {
    try {
        if (itens.length > 0) sessionStorage.setItem(CHAVE, JSON.stringify(itens));
        else sessionStorage.removeItem(CHAVE);
        return true;
    } catch {
        return false;
    }
}

const mesmo = (r: RascunhoNaFila, atendimentoId: string, modeloId: string) => r.atendimentoId === atendimentoId && r.modeloId === modeloId;

export function rascunhosNaFila(): RascunhoNaFila[] {
    return ler();
}

export function rascunhoNaFila(atendimentoId: string, modeloId: string): RascunhoNaFila | undefined {
    return ler().find((r) => mesmo(r, atendimentoId, modeloId));
}

/** Guarda a última versão do rascunho. `false` quando o navegador recusa guardar. */
export function enfileirarRascunho(atendimentoId: string, modeloId: string, respostas: Record<string, RespostaDaFicha>): boolean {
    const outros = ler().filter((r) => !mesmo(r, atendimentoId, modeloId));
    return gravar([...outros, { atendimentoId, modeloId, respostas, guardadoEm: new Date().toISOString() }]);
}

export function tirarDaFila(atendimentoId: string, modeloId: string): void {
    gravar(ler().filter((r) => !mesmo(r, atendimentoId, modeloId)));
}

/**
 * Envia a fila, um por um. O que falhar fica para a próxima volta da rede; o
 * que foi reescrito durante o envio também fica, porque é mais novo.
 */
export async function enviarFila(enviar: (item: RascunhoNaFila) => Promise<void>): Promise<{ enviados: number; falhas: string[] }> {
    let enviados = 0;
    const falhas: string[] = [];
    for (const item of ler()) {
        try {
            await enviar(item);
            if (rascunhoNaFila(item.atendimentoId, item.modeloId)?.guardadoEm === item.guardadoEm) tirarDaFila(item.atendimentoId, item.modeloId);
            enviados += 1;
        } catch (e) {
            falhas.push(e instanceof Error ? e.message : 'Não foi possível enviar o rascunho.');
        }
    }
    return { enviados, falhas };
}
